import { api, type Page } from "@/lib/api/client";
import type { FileScanState } from "@altek/ui/models/files/file-scan";
import { waitForScan } from "@/lib/api/file-scan";
import { apiUpload } from "@/lib/api/upload";
import { env } from "@/lib/env";
import type {
  AttachmentPurpose,
  DirectoryOrgUnit,
  MyProfile,
  PeopleFilters,
  PersonCard,
  PhotoConstraints,
  UpdateProfileInput,
  UploadIntent,
} from "./directory.types";

/**
 * Directory endpoints — the people list, one card, and the caller's own profile.
 *
 * One function per route, no cleverness, so "where is the photo call" is a search rather than a
 * reading exercise. Query strings are always built by the client from an object: a name with a
 * `&` in it concatenated by hand is a request the server reads differently from the one meant.
 *
 * `directoryApi.people` and `directoryApi.person`, `PersonAvatar` and `photoUrlFor` are the
 * four things other features are expected to import from this folder — chat and notes draw the
 * same faces beside the same names, and a second avatar implementation is a second set of
 * initials rules and a second cache-busting convention.
 */

const BASE = "/directory";

/** The purpose slot and subject the profile photo is uploaded under (BACKEND_CONTRACT §9). */
export const PROFILE_PHOTO_PURPOSE = "profile_photo";
export const PROFILE_PHOTO_ENTITY_TYPE = "directory.user_profile";

/**
 * What the picker enforces when it cannot read the catalogue.
 *
 * `GET /files/purposes` is gated behind `files.file.read`, and somebody who may only manage
 * their own profile does not necessarily hold it — so a hard dependency on the catalogue would
 * leave the most ordinary user with no client-side check at all. These mirror the
 * `profile_photo` slot in `files.purposes.ts`; the server re-validates either way, which is
 * what makes falling back honest rather than a guess with consequences.
 */
export const PROFILE_PHOTO_FALLBACK: PhotoConstraints = {
  mimeTypes: ["image/jpeg", "image/png", "image/webp"],
  maxBytes: 2 * 1024 * 1024,
};

export const directoryApi = {
  people(filters: PeopleFilters): Promise<Page<PersonCard>> {
    return api.page<PersonCard>(`${BASE}/people`, { params: { ...filters } });
  },

  person(userId: string): Promise<PersonCard> {
    return api.get<PersonCard>(`${BASE}/people/${encodeURIComponent(userId)}`);
  },

  me(): Promise<MyProfile> {
    return api.get<MyProfile>(`${BASE}/me`);
  },

  /**
   * The profile edit. `version` travels with it and the server refuses a mismatch with 409
   * `STALE_VERSION` — a profile is edited from two tabs more often than anything else in this
   * product, because people leave it open while they hunt for a photograph.
   *
   * A profile that has never been saved has no row yet, and the server answers `version: 0`
   * for it; the first PATCH creates the row. So zero is a real version, never a "missing" one.
   */
  updateMe(input: UpdateProfileInput): Promise<MyProfile> {
    return api.patch<MyProfile>(`${BASE}/me`, input);
  },

  /** Links an already-uploaded, completed file as the photo. Answers the refreshed profile. */
  setPhoto(fileId: string): Promise<MyProfile> {
    return api.put<MyProfile>(`${BASE}/me/photo`, { fileId });
  },

  removePhoto(): Promise<void> {
    return api.delete(`${BASE}/me/photo`);
  },

  /** The upload-slot catalogue, filtered to this screen's subject. Needs `files.file.read`. */
  photoPurposes(): Promise<AttachmentPurpose[]> {
    return api.get<AttachmentPurpose[]>("/files/purposes", {
      params: { entityType: PROFILE_PHOTO_ENTITY_TYPE },
    });
  },

  uploadIntent(input: {
    filename: string;
    contentType: string;
    byteSize: number;
  }): Promise<UploadIntent> {
    return api.post<UploadIntent>("/files/upload-intent", {
      ...input,
      purpose: PROFILE_PHOTO_PURPOSE,
      /**
       * NO `entityId`. The profile row may not exist yet, and for this family of purposes the
       * owning module does the linking: intent → PUT → complete → `PUT /directory/me/photo`.
       * Sending an id here would ask the file service to link something it does not own.
       */
      entityType: PROFILE_PHOTO_ENTITY_TYPE,
    });
  },

  completeUpload(fileId: string): Promise<unknown> {
    return api.post(`/files/${encodeURIComponent(fileId)}/complete`, {});
  },

  /**
   * Org units, for the directory's own filter.
   *
   * Deliberately re-declared here rather than imported from the administration feature: a
   * feature module must not import another one, and this reads two columns of a list the
   * administration screens render in full. The parameters match the administration query
   * exactly so both share one cache entry under `keys.organization.orgUnits`.
   */
  orgUnits(legalEntityId: string): Promise<Page<DirectoryOrgUnit>> {
    return api.page<DirectoryOrgUnit>("/organization/org-units", {
      params: { legalEntityId, status: "ACTIVE", limit: 100 },
    });
  },
};

/**
 * The `<img src>` for a person's photograph.
 *
 * THE ONLY WAY A PHOTO IS DISPLAYED. Presigned download URLs force `Content-Disposition:
 * attachment` and the local delivery route forces `application/octet-stream`, so neither works
 * inside an `<img>`; this route answers `image/*` inline, authenticated by the session cookie
 * the browser already sends. Callers must check `hasPhoto` first — a person with no photograph
 * answers 404, and a broken-image glyph in a directory of two hundred faces is worse than the
 * initials it replaced.
 *
 * `version` is the card's `photoFileId`. The response is cached privately for ten minutes with
 * the file's checksum as its ETag, so without a changing query the browser would keep showing
 * the old face for those ten minutes after a replacement. The server ignores the parameter.
 */
export function photoUrlFor(userId: string, version?: string | null): string {
  const url = `${env.apiBaseUrl}${BASE}/people/${encodeURIComponent(userId)}/photo`;
  return version ? `${url}?v=${encodeURIComponent(version)}` : url;
}

/** The constraints the picker enforces: the catalogue's when readable, the documented ones otherwise. */
export function photoConstraintsFrom(
  purposes: AttachmentPurpose[] | undefined,
): PhotoConstraints {
  /**
   * `Array.isArray` and not `purposes?.`: `/files/purposes` is a shared endpoint this feature
   * does not own, and its 200 declares no body in the generated document — nothing guarantees
   * an array at compile time. A `.find` on an object would throw inside the picker's render and
   * take the whole Profilim page down, unsaved text included, for a catalogue the screen is
   * designed to work without.
   */
  const list = Array.isArray(purposes) ? purposes : [];
  const slot = list.find((purpose) => purpose.key === PROFILE_PHOTO_PURPOSE);
  if (!slot || slot.mimeTypes.length === 0 || slot.maxBytes <= 0) return PROFILE_PHOTO_FALLBACK;
  return { mimeTypes: slot.mimeTypes, maxBytes: slot.maxBytes };
}

/**
 * The three-step upload, start to finish, answering the `fileId` the profile then links.
 *
 * THE BYTES GO THROUGH `apiUpload` (`@/lib/api/upload`), not through a transfer written here.
 * This file used to carry its own `XMLHttpRequest` — open, replay the signed headers, listen for
 * progress, map the failures — which is the shared helper line for line, minus two things it
 * gets right: it skips the whole set of headers a browser refuses to let script set (not just
 * `Content-Length`), and it sends the CSRF header when the presigned URL is same-origin, which
 * is exactly the case of the LOCAL storage driver every developer machine runs. The copy here
 * was refused by the CSRF guard on that driver and worked on S3, which is the worst shape a
 * defect can have.
 *
 * `onProgress` REPORTS WHOLE PERCENT, which is the shared helper's unit and the unit
 * `photo-upload-model.ts` stores. The ratio this used to hand back was divided again by the
 * component and rounded a third time by the kit's progress row.
 */
export async function uploadProfilePhoto(
  file: File,
  onProgress?: (percent: number) => void,
  onScanState?: (state: FileScanState) => void,
): Promise<string> {
  const intent = await directoryApi.uploadIntent({
    filename: file.name,
    contentType: file.type,
    byteSize: file.size,
  });

  await apiUpload(
    { url: intent.uploadUrl, method: intent.method, headers: intent.headers },
    file,
    { onProgress },
  );

  /**
   * Completion is what moves the file to `CLEAN`. Skipping it leaves a row the linking call
   * refuses with 409 `FILE_NOT_READY`, which reads as "the photo is broken" when the truth is
   * "the upload was never declared finished".
   */
  await directoryApi.completeUpload(intent.fileId);
  /**
   * TARAMA (K3): `complete` dosyayı AÇMIYOR, taramaya veriyor. Dosya CLEAN olmadan bağlanamıyor,
   * gönderilemiyor, indirilemiyor — bu yüzden akış kararı bekliyor. Ret, sunucunun kendi koduyla
   * (`FILE_INFECTED`, `FILE_ACTIVE_CONTENT`, `FILE_ENCRYPTED`, `FILE_SCAN_FAILED`) bir `ApiError`
   * olarak yukarı çıkıyor ve cümlesini ortak hata kataloğundan alıyor.
   */
  await waitForScan(intent.fileId, onScanState ? { onState: onScanState } : {});
  return intent.fileId;
}
